import { migrateSheetLayers } from '../../logic/LayerLogic';
import type { Sheet, SheetHistoryEntry, SheetSnapshot } from '../../types';
import { normalizeIncomingColorFields, normalizeSheetColors } from './normalizeSheetColors';
import { stripSheetUiFlags } from './stripUiFlags';
import type { ApplySheetFromJsonParams, ApplySheetFromJsonResult } from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseDateField(value: unknown, fieldName: string): Date | string {
  if (value instanceof Date) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return `Invalid date for ${fieldName}`;
    }
    return parsed;
  }
  return `Missing or invalid ${fieldName}`;
}

function normalizeSheetDates(raw: Record<string, unknown>): { sheet?: Sheet; error?: string } {
  const createdAt = parseDateField(raw.createdAt, 'createdAt');
  if (typeof createdAt === 'string') return { error: createdAt };

  let lastModified: Date | undefined;
  if (raw.lastModified != null) {
    const parsed = parseDateField(raw.lastModified, 'lastModified');
    if (typeof parsed === 'string') return { error: parsed };
    lastModified = parsed;
  }

  const historyRaw = raw.history;
  if (historyRaw != null && !Array.isArray(historyRaw)) {
    return { error: 'history must be an array' };
  }
  const history: SheetHistoryEntry[] = [];
  for (let i = 0; i < (historyRaw?.length ?? 0); i++) {
    const entry = historyRaw![i];
    if (!isRecord(entry)) {
      return { error: `history[${i}] must be an object` };
    }
    const timestamp = parseDateField(entry.timestamp, `history[${i}].timestamp`);
    if (typeof timestamp === 'string') return { error: timestamp };
    history.push({
      ...(entry as unknown as SheetHistoryEntry),
      timestamp,
    });
  }

  const snapshotsRaw = raw.snapshots;
  if (snapshotsRaw != null && !Array.isArray(snapshotsRaw)) {
    return { error: 'snapshots must be an array' };
  }
  const snapshots: SheetSnapshot[] = [];
  for (let i = 0; i < (snapshotsRaw?.length ?? 0); i++) {
    const snapshot = snapshotsRaw![i];
    if (!isRecord(snapshot)) {
      return { error: `snapshots[${i}] must be an object` };
    }
    const timestamp = parseDateField(snapshot.timestamp, `snapshots[${i}].timestamp`);
    if (typeof timestamp === 'string') return { error: timestamp };
    snapshots.push({
      ...(snapshot as unknown as SheetSnapshot),
      timestamp,
    });
  }

  const sheet = {
    ...(raw as unknown as Sheet),
    createdAt,
    lastModified,
    history,
    snapshots,
  };

  return { sheet };
}

function validatePouch(pouch: unknown, index: number): string | null {
  if (!isRecord(pouch)) return `pouches[${index}] must be an object`;
  if (typeof pouch.id !== 'string' || !pouch.id.trim()) {
    return `pouches[${index}].id must be a non-empty string`;
  }
  const hasX = typeof pouch.x === 'number' && !Number.isNaN(pouch.x);
  const hasY = typeof pouch.y === 'number' && !Number.isNaN(pouch.y);
  if (hasX !== hasY) {
    return `pouches[${index}]: x and y must both be provided or both omitted`;
  }
  for (const key of ['value', 'multiplier'] as const) {
    if (typeof pouch[key] !== 'number' || Number.isNaN(pouch[key] as number)) {
      return `pouches[${index}].${key} must be a number`;
    }
  }
  if (hasX) {
    for (const key of ['x', 'y'] as const) {
      if (typeof pouch[key] !== 'number' || Number.isNaN(pouch[key] as number)) {
        return `pouches[${index}].${key} must be a number`;
      }
    }
  }
  if (typeof pouch.isNegative !== 'boolean') {
    return `pouches[${index}].isNegative must be a boolean`;
  }
  if (typeof pouch.color !== 'string' || !pouch.color.trim()) {
    return `pouches[${index}].color must be a non-empty string`;
  }
  return null;
}

function validateGroup(group: unknown, index: number): string | null {
  if (!isRecord(group)) return `groups[${index}] must be an object`;
  if (typeof group.id !== 'string' || !group.id.trim()) {
    return `groups[${index}].id must be a non-empty string`;
  }
  if (typeof group.path !== 'string') {
    return `groups[${index}].path must be a string`;
  }
  if (!Array.isArray(group.pouches)) {
    return `groups[${index}].pouches must be an array`;
  }
  for (let j = 0; j < group.pouches.length; j++) {
    if (typeof group.pouches[j] !== 'string') {
      return `groups[${index}].pouches[${j}] must be a string`;
    }
  }
  return null;
}

function normalizeAutoPlacePouches(raw: Record<string, unknown>): string[] {
  const autoPlacePouchIds: string[] = [];
  if (!Array.isArray(raw.pouches)) return autoPlacePouchIds;
  for (const pouch of raw.pouches) {
    if (!isRecord(pouch) || typeof pouch.id !== 'string') continue;
    const hasX = typeof pouch.x === 'number' && !Number.isNaN(pouch.x);
    const hasY = typeof pouch.y === 'number' && !Number.isNaN(pouch.y);
    if (!hasX && !hasY) {
      pouch.x = 0;
      pouch.y = 0;
      autoPlacePouchIds.push(pouch.id);
    }
  }
  return autoPlacePouchIds;
}

function validateSheetReferences(sheet: Sheet): string | null {
  const pouchIds = new Set<string>();
  for (const pouch of sheet.pouches) {
    if (pouchIds.has(pouch.id)) {
      return `Duplicate pouch id: ${pouch.id}`;
    }
    pouchIds.add(pouch.id);
  }

  const groupIds = new Set<string>();
  for (const group of sheet.groups) {
    if (groupIds.has(group.id)) {
      return `Duplicate group id: ${group.id}`;
    }
    groupIds.add(group.id);
  }

  for (const group of sheet.groups) {
    for (const pouchId of group.pouches ?? []) {
      if (!pouchIds.has(pouchId)) {
        return `Group ${group.id} references unknown pouch id: ${pouchId}`;
      }
    }
  }

  for (const pouch of sheet.pouches) {
    if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
      if (!groupIds.has(pouch.referencedGroupId)) {
        return `Pouch ${pouch.id} references unknown group id: ${pouch.referencedGroupId}`;
      }
    }
  }

  if (sheet.history.length === 0) {
    if (
      sheet.historyIndex != null &&
      sheet.historyIndex !== -1 &&
      sheet.historyIndex !== 0
    ) {
      return 'historyIndex must be -1 or 0 when history is empty';
    }
  } else if (sheet.historyIndex != null) {
    if (typeof sheet.historyIndex !== 'number' || !Number.isInteger(sheet.historyIndex)) {
      return 'historyIndex must be an integer';
    }
    if (sheet.historyIndex < 0 || sheet.historyIndex >= sheet.history.length) {
      return 'historyIndex is out of range for history length';
    }
  }

  return null;
}

function validateSheetSchema(raw: Record<string, unknown>): string | null {
  if (!Array.isArray(raw.pouches)) return 'pouches must be an array';
  if (!Array.isArray(raw.groups)) return 'groups must be an array';

  for (let i = 0; i < raw.pouches.length; i++) {
    const err = validatePouch(raw.pouches[i], i);
    if (err) return err;
  }
  for (let i = 0; i < raw.groups.length; i++) {
    const err = validateGroup(raw.groups[i], i);
    if (err) return err;
  }

  return null;
}

function resolveRevision(sheet: Sheet, currentRevision?: number): number {
  if (typeof currentRevision === 'number' && !Number.isNaN(currentRevision)) {
    return currentRevision;
  }
  if (typeof sheet.revision === 'number' && !Number.isNaN(sheet.revision)) {
    return sheet.revision;
  }
  return 0;
}

/**
 * Sheet JSON の検証・正規化（純関数）。
 * IndexedDB / React には触れない。統合レイヤが IDB 保存と UI 反映を行う。
 */
export function applySheetFromJson(params: ApplySheetFromJsonParams): ApplySheetFromJsonResult {
  const { sheetId, sheetJson, currentRevision, expectedRevision } = params;

  if (!sheetId.trim()) {
    return { ok: false, code: 'VALIDATION_ERROR', message: 'sheetId is required' };
  }

  if (!isRecord(sheetJson)) {
    return { ok: false, code: 'VALIDATION_ERROR', message: 'sheet must be a JSON object' };
  }

  if (typeof sheetJson.id !== 'string' || sheetJson.id !== sheetId) {
    return {
      ok: false,
      code: 'SHEET_ID_MISMATCH',
      message: `sheet.id must equal sheetId (expected ${sheetId}, got ${String(sheetJson.id)})`,
    };
  }

  normalizeIncomingColorFields(sheetJson);

  const schemaError = validateSheetSchema(sheetJson);
  if (schemaError) {
    return { ok: false, code: 'VALIDATION_ERROR', message: schemaError };
  }

  const autoPlacePouchIds = normalizeAutoPlacePouches(sheetJson);

  const { sheet: datedSheet, error: dateError } = normalizeSheetDates(sheetJson);
  if (dateError || !datedSheet) {
    return { ok: false, code: 'VALIDATION_ERROR', message: dateError ?? 'Invalid sheet dates' };
  }

  const baseRevision = resolveRevision(datedSheet, currentRevision);
  if (expectedRevision !== undefined && expectedRevision !== baseRevision) {
    return {
      ok: false,
      code: 'REVISION_CONFLICT',
      message: `Expected revision ${expectedRevision} but current is ${baseRevision}`,
    };
  }

  const refError = validateSheetReferences(datedSheet);
  if (refError) {
    return { ok: false, code: 'VALIDATION_ERROR', message: refError };
  }

  const stripped = stripSheetUiFlags(datedSheet);
  const migrated = migrateSheetLayers(stripped);
  const colored = normalizeSheetColors(migrated);
  const nextRevision = baseRevision + 1;

  const sheet: Sheet = {
    ...colored,
    id: sheetId,
    revision: nextRevision,
    history: migrated.history ?? [],
    historyIndex: migrated.historyIndex ?? (migrated.history?.length ? 0 : -1),
    snapshots: migrated.snapshots ?? [],
  };

  return {
    ok: true,
    sheetId,
    revision: nextRevision,
    sheet,
    autoPlacePouchIds,
  };
}
